import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import Select, { ActionMeta, components, FormatOptionLabelMeta, InputActionMeta, MultiValue, OptionProps, SingleValue, StylesConfig } from 'react-select';

import "./_react-select.scss";

export type ReactSelectOption = { value: string; label: string; hint?: string, isDisabled?: boolean, isHidden?: boolean, className?: string };
export type ReactSelectValue = string | null;
export type ReactSelectOptions = ReactSelectOption[] | null;
export type ReactSelectCallback = (value: ReactSelectValue, options: ReactSelectOptions) => void;

export type ReactSelectProps<T> = {
    placeholder?: string;
    value?: ReactSelectValue;
    multi?: boolean;
    isClearable?: boolean;
    closeOnSelect?: boolean;
    isSearchable?: boolean;
    isCreatable?: boolean;
    backspaceRemovesValue?: boolean;
    hideSelectedOptions?: boolean;
    tabSelectsValue?: boolean;
    menuPortalTarget?: HTMLElement;
    options: T[];
    onSelect?: ReactSelectCallback;
    styles?: StylesConfig<T, boolean>;
    isDisabled?: boolean;
    isLoading?: boolean;
    isOptionDisabled?: (option: T) => boolean;
    className?: string;
    inputValue?: string;
    onMenuOpen?: () => void;
    onInputChange?: (value: string) => void;
    formatOptionLabel?: (data: T, formatOptionLabelMeta: FormatOptionLabelMeta<T>) => React.ReactNode;
};

@observer
export default class ReactSelect<T extends ReactSelectOption> extends React.Component<ReactSelectProps<T>, {}> {
    private _store: ReactSelectStore;

    constructor(props: ReactSelectProps<T>) {
        super(props);
        this._store = new ReactSelectStore(this.props.value);
    }

    componentDidUpdate(prevProps: ReactSelectProps<T>) {
        if (prevProps.value !== this.props.value) {
            this._store.updateValue(this.props.value);
        }

        if (prevProps.inputValue !== this.props.inputValue) {
            this._store.updateInputValue(this.props.inputValue);
        }
    }

    render() {
        const { multi, isClearable, closeOnSelect, placeholder, isSearchable, backspaceRemovesValue, hideSelectedOptions,
            styles, isDisabled, className, isOptionDisabled, isCreatable, isLoading, formatOptionLabel,
            tabSelectsValue, menuPortalTarget
        } = this.props;
        const classList = ['select-container'];
        if (className) classList.push(className);

        const Option = (props: OptionProps<T>) => {
            return (
                <div title={props.data.hint ?? ''} hidden={props.data.isHidden} className={props.data.className}>
                    <components.Option {...props} />
                </div>
            );
        };

        return (
            <Select
                styles={styles}
                isDisabled={isDisabled}
                className={classList.join(' ')}
                classNamePrefix="select-filter"
                placeholder={placeholder || ""}
                menuPortalTarget={menuPortalTarget}
                closeMenuOnSelect={closeOnSelect || !multi}
                isMulti={!!multi}
                isClearable={!!isClearable}
                hideSelectedOptions={!!hideSelectedOptions}
                backspaceRemovesValue={!!backspaceRemovesValue}
                isSearchable={isSearchable || isCreatable}
                onChange={this._selectHandler}
                onMenuClose={this._closeHandler}
                onMenuOpen={this._openHandler}
                options={this._options}
                isLoading={isLoading}
                tabSelectsValue={tabSelectsValue}
                isOptionDisabled={isOptionDisabled}
                value={this._selectedOption}
                components={{ Option }}
                onInputChange={this._inputChangeHandler}
                inputValue={this._store.inputValue}
                formatOptionLabel={formatOptionLabel}
            />
        );
    }

    @action.bound
    private _inputChangeHandler(value: string, action: InputActionMeta) {
        const { isCreatable, onInputChange, onSelect } = this.props;
        const { updateValue } = this._store;

        this._store.updateInputValue(value);

        if (isCreatable) {
            if (action?.action === 'input-blur' || action?.action === 'menu-close') return;

            onSelect?.(value, this._selectedOption);
            updateValue(value);
        }

        onInputChange?.(value);
    }

    @action.bound
    private _selectHandler(selected: SingleValue<T> | MultiValue<T>, { action }: ActionMeta<T>) {
        const { onSelect, multi } = this.props;
        const { updateValue } = this._store;
        let newValue: null | string = null;
        if (selected) {
            newValue = Array.isArray(selected) ? selected.map((el) => el.value).join(',') : (selected as T).value;
        }
        updateValue(newValue);
        const isClearAction = action === 'clear' || action === 'remove-value';
        if (!multi || isClearAction) {
            onSelect && onSelect(newValue, this._selectedOption);
        }
    }

    @action.bound
    private _closeHandler() {
        const { multi, onSelect } = this.props;
        const { currentValue, defaultValue, updateDefaultValue } = this._store;
        if (!multi) return;
        if (currentValue && currentValue !== defaultValue) {
            onSelect && onSelect(currentValue, this._selectedOption);
        } else if (!currentValue && defaultValue) {
            onSelect && onSelect(null, null);
        }
        updateDefaultValue(null);
    }

    @action.bound
    private _openHandler() {
        const { multi, value, isCreatable, onMenuOpen } = this.props;
        const { currentValue, updateInputValue, updateDefaultValue } = this._store;

        if (isCreatable && currentValue) {
            updateInputValue(currentValue);
        }

        if (multi) {
            updateDefaultValue(value);
        }
        onMenuOpen && onMenuOpen();
    }

    private get _options() {
        const options = [...this.props.options];

        if (this.props.isCreatable) {
            const { currentValue } = this._store;
            currentValue && options.push({ value: currentValue, label: currentValue } as T);
        }

        return options;
    }

    private get _selectedOption() {
        const { currentValue } = this._store;
        const values = (currentValue && currentValue.split(',')) || [];
        const options = this._options.filter((el) => values.includes(el.value));
        return options.length ? options : null;
    }

    public static GenerateOptionsFrom<T>(obj: T[], valKey?: keyof T, labKey?: keyof T): ReactSelectOption[] {
        if (obj.length && typeof obj[0] === "string") {
            return obj.map(e => {
                const value = e as unknown as string;
                return { value, label: value };
            }
            );
        } else {
            if (!valKey || !labKey) return [];
            return obj.map(e => {
                const value = e[valKey] as unknown as string;
                const label = e[labKey] as unknown as string;
                return { value, label };
            });
        }
    }
}

class ReactSelectStore {
    @observable currentValue: ReactSelectValue = null;
    @observable defaultValue: ReactSelectValue = null;
    @observable inputValue: string = '';

    constructor(value?: ReactSelectValue) {
        this.updateValue(value);
    }

    @action.bound
    updateValue(value?: ReactSelectValue) {
        if (this.currentValue !== value) {
            this.currentValue = value || null;
        }
    }

    @action.bound
    updateDefaultValue(value?: ReactSelectValue) {
        this.defaultValue = value || null;
    }

    @action.bound
    updateInputValue(value?: string) {
        this.inputValue = value ?? '';
    }
}
